import { useRef, useState } from "react";
import classNames from "classnames";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import CanvasIcon from "./CanvasIcon";
import WorkshopScreenCard, { CARD_TONES } from "./WorkshopScreenCard";
import {
  fieldCountText,
  isSlotCollapse,
  PROPOSAL_KINDS,
  WORKSHOP_ICONS,
} from "./workshop-view";

export const SIDES = { BEFORE: "before", AFTER: "after" };

const HOLD_MS = 250;

const keyOf = (item, index) => item.targetId || `${index}`;

export const CompareToggle = ({ labels, side, onSide }) => {
  const pressedAt = useRef(0);
  const holding = useRef(false);
  return (
    <div
      role="group"
      aria-label={labels.COMPARE}
      className="workshop-proposal__compare"
    >
      <button
        type="button"
        aria-pressed={side === SIDES.BEFORE}
        onMouseDown={() => {
          pressedAt.current = Date.now();
          holding.current = true;
          onSide(SIDES.BEFORE);
        }}
        onMouseUp={() => {
          holding.current = false;
          if (Date.now() - pressedAt.current >= HOLD_MS) onSide(SIDES.AFTER);
        }}
        onMouseLeave={() => {
          if (!holding.current) return;
          holding.current = false;
          onSide(SIDES.AFTER);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") onSide(SIDES.BEFORE);
        }}
      >
        {labels.BEFORE}
      </button>
      <button
        type="button"
        aria-pressed={side === SIDES.AFTER}
        onClick={() => onSide(SIDES.AFTER)}
      >
        {labels.AFTER}
      </button>
    </div>
  );
};

CompareToggle.propTypes = {
  labels: PropTypes.object.isRequired,
  side: PropTypes.oneOf(Object.values(SIDES)).isRequired,
  onSide: PropTypes.func.isRequired,
};

const WorkshopProposal = ({
  proposals,
  styleContent,
  labels,
  captions,
  defaultName,
  applying,
  describe,
  onApply,
  onCancel,
}) => {
  const [selected, setSelected] = useState(0);
  const [excluded, setExcluded] = useState([]);
  const [side, setSide] = useState(SIDES.AFTER);

  if (!proposals.length) return null;

  const index = Math.min(selected, proposals.length - 1);
  const proposal = proposals[index];
  const nameOf = (item) => item.targetName || defaultName;
  const previous = get(proposal, "previousHtmlContent");
  const showingBefore = side === SIDES.BEFORE && previous;
  const kept = proposals.filter(
    (item, position) => excluded.indexOf(keyOf(item, position)) < 0,
  );
  const collapsed = kept.filter(isSlotCollapse);
  const batchStyle = get(
    proposals.find((item) => item.styleContent),
    "styleContent",
    null,
  );
  const isState = proposal.kind === PROPOSAL_KINDS.STATE && proposal.isNew;
  const trigger = isState ? describe(proposal.conditions) : null;
  const isBody = proposal.kind === PROPOSAL_KINDS.VERSION;
  const applyOff = !kept.length || applying;
  const shownCount = get(
    proposal,
    showingBefore ? "slotsBefore" : "slotsAfter",
    null,
  );

  const collapseText = labels.COLLAPSE.replace(
    "{{targets}}",
    collapsed
      .map(
        (item) => `${nameOf(item)} (${item.slotsBefore} → ${item.slotsAfter})`,
      )
      .join(", "),
  );

  return (
    <>
      <div className="workshop-proposal">
        <div className="workshop-proposal__head">
          <span className="workshop-proposal__icon">
            <CanvasIcon path={WORKSHOP_ICONS.sparkle} />
          </span>
          <div className="workshop-proposal__text">
            <strong>{labels.TITLE}</strong>
            <span>{labels.HINT}</span>
          </div>
          <button
            type="button"
            className="workshop-proposal__cancel"
            disabled={applying}
            onClick={onCancel}
          >
            {labels.CANCEL}
          </button>
          <button
            type="button"
            className="workshop-proposal__apply"
            disabled={applyOff}
            onClick={() => onApply(kept)}
          >
            <CanvasIcon path={WORKSHOP_ICONS.check} strokeWidth={2.2} />
            {proposals.length > 1
              ? labels.APPLY_PART.replace("{{kept}}", kept.length).replace(
                  "{{total}}",
                  proposals.length,
                )
              : labels.APPLY}
          </button>
        </div>

        <div className="workshop-proposal__items">
          {proposals.map((item, position) => {
            const key = keyOf(item, position);
            const isKept = excluded.indexOf(key) < 0;
            const isOn = position === index;
            const drop = isSlotCollapse(item);
            return (
              <div
                key={key}
                className={classNames("workshop-proposal__item", {
                  "workshop-proposal__item--on": isOn,
                  "workshop-proposal__item--off": !isKept,
                })}
              >
                <input
                  type="checkbox"
                  checked={isKept}
                  disabled={applying}
                  aria-label={labels.KEEP.replace("{{name}}", nameOf(item))}
                  onChange={() =>
                    setExcluded((current) =>
                      isKept
                        ? [...current, key]
                        : current.filter((entry) => entry !== key),
                    )
                  }
                />
                <button
                  type="button"
                  aria-pressed={isOn}
                  onClick={() => {
                    setSelected(position);
                    setSide(SIDES.AFTER);
                  }}
                >
                  <span className="workshop-proposal__name">
                    {nameOf(item)}
                    {item.isNew ? (
                      <span className="workshop-proposal__new">
                        {labels.NEW}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={classNames("workshop-proposal__meta", {
                      "workshop-proposal__meta--warn": drop,
                    })}
                  >
                    {drop ? (
                      <CanvasIcon
                        path={WORKSHOP_ICONS.warn}
                        size={13}
                        strokeWidth={2.2}
                      />
                    ) : null}
                    {fieldCountText(item, labels)}
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        {collapsed.length ? (
          <div className="workshop-proposal__warn">
            <CanvasIcon path={WORKSHOP_ICONS.warn} size={16} strokeWidth={2} />
            <span>{collapseText}</span>
          </div>
        ) : null}

        <div className="workshop-proposal__foot">
          {isState && trigger ? (
            <span className="workshop-proposal__trigger">
              <CanvasIcon
                path={WORKSHOP_ICONS.info}
                size={14}
                strokeWidth={2}
              />
              {labels.TRIGGER.replace("{{condition}}", trigger)}
            </span>
          ) : null}
          {isState && !trigger ? (
            <span className="workshop-proposal__trigger workshop-proposal__trigger--none">
              {labels.NO_TRIGGER}
            </span>
          ) : null}
          {isBody ? (
            <span className="workshop-proposal__body">{labels.BODY}</span>
          ) : null}
          <span className="workshop-proposal__spacer" />
          {previous ? (
            <>
              <span className="workshop-proposal__hold">{labels.HOLD}</span>
              <CompareToggle labels={labels} side={side} onSide={setSide} />
            </>
          ) : null}
        </div>
      </div>

      <WorkshopScreenCard
        spacious
        html={showingBefore ? previous : proposal.htmlContent}
        styleContent={
          showingBefore
            ? get(proposal, "previousStyleContent") || styleContent
            : proposal.styleContent || batchStyle || styleContent
        }
        caption={(showingBefore ? captions.BEFORE : captions.AFTER).replace(
          "{{target}}",
          nameOf(proposal),
        )}
        count={
          isNil(shownCount)
            ? ""
            : labels.FIELDS.replace("{{count}}", shownCount)
        }
        tone={showingBefore ? CARD_TONES.BEFORE : CARD_TONES.AFTER}
      />
    </>
  );
};

WorkshopProposal.propTypes = {
  proposals: PropTypes.array,
  styleContent: PropTypes.string,
  labels: PropTypes.object.isRequired,
  captions: PropTypes.object.isRequired,
  defaultName: PropTypes.string,
  applying: PropTypes.bool,
  describe: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

WorkshopProposal.defaultProps = {
  proposals: [],
  styleContent: undefined,
  defaultName: "",
  applying: false,
};

export default WorkshopProposal;
